import * as moment from 'moment';
import * as Hls from '../libs/hls.min.js';
import * as isMobile from 'ismobilejs';
import {PlaylistManager, PlaylistType} from "./playlistManager";
import URLIterator from "./URLIterator";
import Auth from './auth';    // 多チャンネル化対応
import * as HlsParser from './hls-parser/index.js'
import RadikoJSPlayer from './index.js';

declare var API_HOST: string;

const PLAYLIST_HTTP_MAX_RETRY = 3;
const HTTP_RETRY_DELAY_MS= 2500;
const _fetch:any = async (url:any, options:{}, n:any) => {
    try {
        console.log("count=", n);
        let res = await fetch(url, options);
        if(res.status>=400){
            if (n === 1){
                console.log("_fetch: retry over!");
                return res;
            }
            await new Promise(r => setTimeout(r, HTTP_RETRY_DELAY_MS));
            return await _fetch(url, options, n - 1);
        }
        return res;

    } catch (err) {
        if (n === 1){
            console.log("_fetch: retry over!");
            throw err;
        }
        await new Promise(r => setTimeout(r, HTTP_RETRY_DELAY_MS));
        return await _fetch(url, options, n - 1);
    }
};

interface PlayerCallbacks {
    isStationInArea?: (areaId: string, stationId: string) => boolean,
    isRadikoAreafree?: () => boolean,
    onHLSPlaybackComplete?: any,
    onPlayStateChange?: any,
    onLoadStateError?: any,
    onCurrentTimeChange?: any,
    onFragmentPlaying?: any,
    onChunkListLoaded?: any,
    onLoadStateReady?: any,
    onAuthFailure?: any,
}

class NativePlayerState {
    public mediaPlaylistUrl: string | null;
    public programDatetime: string | null;
    public segment: any;
    public playlist: any;
    constructor() {
        this.mediaPlaylistUrl = null;
        this.programDatetime = null;
        this.segment = null;
        this.playlist = null;
    }
    public get isPlayable(): boolean {
        return (this.mediaPlaylistUrl!=null && this.mediaPlaylistUrl.length>0 && this.segment!=null);
    }
}

export default class Player {
    private _authToken: string;
    /**
     * hls.jsの型定義がHTMLVideoElementしか受け付けないので仕方なくanyにする
     */
    private _audio: any;
    private _hls: Hls;
    /**
     * ライブの際の、再生基準位置
     * audio.currentTimeが相対時間になってしまうので、その対応
     * @type {number}
     * @private
     */
    private _liveBaseSec = 0;
    private _url: string = '';
    private _areaId: string = '';
    private _lsId: string = '';
    private _length: number = 15;
    private _host: string;
    private _appId: string;
    private _playlistManager: PlaylistManager;
    private requestUrls: URLIterator;
    private _isMuted: boolean = false;
    private _isPlayStarted: boolean = false;

    public _isLive: boolean = false;

    public _connectionType: string;

    public _lastAudioEventType: string = '';
    private _playStateChangeHandler: any;
    public _callbacks?: PlayerCallbacks;
    private _audioCallbacksBinded: boolean = false;
    private _hlsCallbacksBinded: boolean = false;

    // 多チャンネル化対応
    public _auth: Auth;
    public _isSharePlay?: boolean = false;
    public _options: any;

    private _nativeState: NativePlayerState;
    private _is_idle: boolean = false;
    private _idleAudioSrc = "data:audio/wav;base64,UklGRk4AAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAATElTVBoAAABJTkZPSVNGVA4AAABMYXZmNTguNzYuMTAwAGRhdGEIAAAAAAAAAAAAAAA=";
    //private _idleAudioSrc = "data:audio/mpeg;base64,";

    constructor(audio: any, authToken: string, auth: Auth, callbacks?: PlayerCallbacks) {
        this._authToken = authToken;
        this._auth = auth;    // 多チャンネル化対応
        this._host = typeof API_HOST === 'undefined' ? location.host : API_HOST;
        // Mobile SafariだとHlsjsでは動かないのでネイティブを使用するようにする
        if (Hls.isSupported() && !isMobile.apple.device) {
            this._hls = this.createHls();
        }
        this._audio = audio;
        this._audio.volume = 0.5;
        this._audio.muted = true;
        this._audio.preload = "none";

        this._callbacks = callbacks;
        this._nativeState = new NativePlayerState();

        let playlistManagerCallback;
        if (callbacks) {
            playlistManagerCallback = {
                isAreaFree: callbacks.isRadikoAreafree,
                containStation: callbacks.isStationInArea
            };

            // 多チャンネル化対応
            this._options = callbacks;
        }
        this._playlistManager = new PlaylistManager(playlistManagerCallback);
    }

    public get lsId(): string {
        return this._lsId;
    }

    public set lsId(value: string) {
        this._lsId = value;
    }

    public get length(): number {
        return this._length;
    }

    public set length(value: number) {
        this._length = value;
    }

    public set appId(value: string) {
        this._appId = value;
    }

    private createHls(): Hls {
        // 文字列のチェックはtestを使うようにとMDNに書かれていたのでtestでチェックする
        // 参考: https://developer.mozilla.org/ja/docs/Web/JavaScript/Reference/Global_Objects/String/match
        const debug = !/radiko\.jp/.test(location.host);
        const hls = new Hls({
            debug: debug,
            xhrSetup: (xhr: any, url: any) => {
                if (url.match(/playlist.m3u8/) !== null) {
                    xhr.setRequestHeader('X-Radiko-AuthToken', this._authToken);
                    xhr.setRequestHeader('X-Radiko-AreaId', this._areaId);
                }

                // wowzaの場合はcookieを使用をしないのでfalseにする
                xhr.withCredentials = url.match(/(wowza|smartstream\.ne\.jp)/) == null;
            },
            maxMaxBufferLength: 30,
            defaultAudioCodec: 'mp4a.40.5',
            fragLoadingMaxRetry: 2,
            levelLoadingMaxRetry: 2
        });

        return hls;
    }

    private hlsPlaybackCompleteHandler(callback: () => void) {

        return () => {
            callback();
        };
    }

    private currentTimeChangeHandler(callback: (t: any) => void) {

        return (e: any) => {
            if (this._audio.paused && !this._audio.ended) {
                return;
            }

            let position;
            let duration;
            if (this._audio.ended) {
                position = (<any>this).totm.diff((<any>this).fttm, 'seconds');
                duration = (<any>this).totm.diff((<any>this).fttm, 'seconds');
            } else {
                position = this.currentTime;
                duration = this.duration;
            }

            callback({
                position: position,
                duration: duration,
                backbuffer: 0,
                buffer: 0,
                live_sliding_altaudio: 0,
                live_sliding_main: 0
            });
        };
    }

    private playStateChangeHandler(callback: (m: string) => void) {
        return (e: any) => {
            let type: string = '';
            switch (e.type) {
                case 'playing':
                    type = 'playing';
                    break;
                case 'pause':
                case 'ended':
                    type = 'stopped';
                    break;
            }
            if(this._lastAudioEventType !== type){
                this._lastAudioEventType = type;
                callback(type);
            }
        }
    }

    private fragmentPlayingHandler(callback: (m: any) => void) {
        return (name: string, detail: any) => {
            if(this._hls){
                callback({
                    program_date: detail ? detail.frag.rawProgramDateTime : (<any>this).fttm ? +(<any>this).fttm.valueOf() : 0
                });
            }else{
                callback({
                    program_date: this._nativeState.programDatetime ? this._nativeState.programDatetime : (<any>this).fttm ? +(<any>this).fttm.valueOf() : 0
                });
            }
        }
    }

    private fragmentLoadedHandler(callback: (level: any) => void) {
        return (e: any, level: any) => {
            callback(level.frag);
        }
    }

    private chunkListLoadedHandler(callback: (details: any) => void) {
        return (e: any, context: any) => {
            if(this._hls){
                callback(context.details);
            }else{
                this._nativeState.segment.tags.forEach((tag:any)=> {
                    if(tag[0]=='AD-ID'){
                        console.log(`######### AD-ID: ${tag[1]} #########`);
                    }
                });
                var fragments:Array<any> = [];
                let segments:Array<any> = Object(this._nativeState.playlist)["segments"];
                segments.forEach((seg: any) => {
                    fragments.push({
                        baseurl: seg.uri,
                        relurl: seg.uri,
                        duration: seg.duration,
                        title: seg.title,
                        rawProgramDateTime: seg.programDateTime,
                        sn: seg.mediaSequenceNumber,
                        tagList: seg.tags
                    });
                });
                callback({
                    fragments: fragments
                });
            }
        }
    }

    private hlsErrorHandler(callback: (m: any) => void) {
        return (name: any, data: any) => {
            if ([Hls.ErrorDetails.MANIFEST_LOAD_TIMEOUT, Hls.ErrorDetails.MANIFEST_LOAD_ERROR].includes(data.details)) {
                const requestUrl = this.requestUrls.next();
                if (requestUrl.done === false) {
                    this.loadHlsjs(requestUrl.value)
                        .then(() => {
                            this.play();
                        });
                    return;
                }
            }
            if (data.fatal === true) {
                callback(data);
            }
        }
    }
    
    private nativeErrorHandler(callback: (m: any) => void) {
        return (e: any) => {
            this.stopPollingNative();
            const requestUrl = this.requestUrls.next();
            if (requestUrl.done === false) {
                this.loadNative(requestUrl.value)
                    .then(() => {
                        this.play();
                    });
                return;
            }
            callback(e);
        }
    }

    private bind(callbacks?: PlayerCallbacks) {
        if (typeof callbacks === 'undefined') {
            return;
        }

        /*['abort',
        'canplay',
        'canplaythrough',
        'durationchange',
        'emptied',
        'ended',
        'error',
        'loadeddata',
        'loadedmetadata',
        'loadstart',
        'pause',
        'play',
        'progress',
        'ratechange',
        'seeked',
        'seeking',
        'stalled',
        'suspend',
        'timeupdate',
        'volumechange',
        'waiting'].forEach((eventName) => {*/

        if(! this._audioCallbacksBinded){
            ['loadstart', 'play', 'canplay', 'pause', 'ended'].forEach((eventName) => {
                this._audio.addEventListener(eventName, {thisPlayer: this, handleEvent: this.onPlayStateChanged.bind(this)});
            });
            if (callbacks.onHLSPlaybackComplete) {
                this._audio.addEventListener('ended', this.hlsPlaybackCompleteHandler(callbacks.onHLSPlaybackComplete));
            }
            if (callbacks.onPlayStateChange) {
                this._playStateChangeHandler = this.playStateChangeHandler(callbacks.onPlayStateChange);
                this._audio.addEventListener('playing', this._playStateChangeHandler);
                this._audio.addEventListener('pause', this._playStateChangeHandler);
                this._audio.addEventListener('ended', this._playStateChangeHandler);
            }
            if (callbacks.onCurrentTimeChange) {
                this._audio.addEventListener('timeupdate', this.currentTimeChangeHandler(callbacks.onCurrentTimeChange));
            }
            if (callbacks.onFragmentPlaying) {
                this._audio.addEventListener('play', this.fragmentPlayingHandler(callbacks.onFragmentPlaying));
            }
            if (callbacks.onLoadStateReady) {
                this._audio.addEventListener('play', callbacks.onLoadStateReady);
            }
            if (callbacks.onLoadStateError) {
                if (! this._hls) {
                    this._audio.addEventListener('error', this.nativeErrorHandler(callbacks.onLoadStateError));
                }
            }
        }
        
        if(this._hls && !this._hlsCallbacksBinded){
            if (callbacks.onFragmentPlaying) {
                this._hls.on(Hls.Events.FRAG_LOADING, this.fragmentPlayingHandler(callbacks.onFragmentPlaying));
            }
            if (callbacks.onChunkListLoaded) {
                this._hls.on(Hls.Events.LEVEL_LOADED, this.chunkListLoadedHandler(callbacks.onChunkListLoaded));
            }
            if (callbacks.onLoadStateError) {
                if(!this._audioCallbacksBinded){
                    this._audio.addEventListener('error', callbacks.onLoadStateError);
                }
                this._hls.on(Hls.Events.ERROR, this.hlsErrorHandler(callbacks.onLoadStateError));
            }
        }
        if (callbacks.onAuthFailure) {
            // this._audio.addEventListener('ended', callbacks.onAuthFailure);
        }
        this._audioCallbacksBinded = true;
        this._hlsCallbacksBinded = true;
    }

    private onPlayStateChanged(event:any) {
        console.log(`[onPlayStateChanged] type:${event.type}, paused:${event.target.paused}, _isPlayStarted=${this._isPlayStarted}`);
        if (this._hls) return;

        if(event.target.stopped || event.type=='error'){
            this.stopPollingNative();
        }
        if(event.type=="play" && this._audio){
            this._audio.muted = this._isMuted;
            if(this._isPlayStarted){
                // 既に再生している場合、マスタープレイリスト取得からやり直す
                this._audio.pause();
                console.log("マスタープレイリスト取得からやり直す");
                setTimeout(()=>{
                    this.stopPollingNative();
                    //this.requestUrls.restart();
                    const requestUrl = this.requestUrls.current();
                    if (this._hls) {
                        this.loadHlsjs(requestUrl.value).then(()=>{
                            this.play();
                        });
                    } else {
                        this.loadNative(requestUrl.value).then(()=>{
                            this.play();
                        });
                    }
                }, 100);
                return;
            }
            this._isPlayStarted = true;
        }
    }

    public async load(url: string) {
        const params = this.parse(url);

        params.set('lsid', this._lsId);

        this._playlistManager.areaId = this._areaId;
        this._playlistManager.stationId = params.get('station_id');
        this._playlistManager.playlistType = params.get('start_at') ? PlaylistType.VOD : PlaylistType.LIVE;
        var urls:any = [];
        try {
            urls = await this.getPlaylistUrl(params.get('station_id'));
        } catch (error) {
            console.log(error)
        }
        if(!urls || urls.length==0){
            if (this._callbacks && this._callbacks.onLoadStateError) {
                this._callbacks.onLoadStateError({});
            }
            throw 'Cannot find resource url';
        }
        // 正常系
        let requestUrls = [];

        // 多チャンネル化対応
        // エリアフリー再生、プレビュー再生ではない場合は station_id を sub_station_id に変更する
        const subStationsStr = this._auth._subStations;
        if (subStationsStr && !this._isSharePlay && this.containStation(this._areaId, params.get('station_id'))) {
            const subStations = subStationsStr.split(',');
            for (let i = 0; i < subStations.length; i++) {
                const subStation = subStations[i].split('/');
                const multiChannelAreaId = subStation[0];
                const multiChannelStationId = subStation[1];
                const multiChannelSubStationId = subStation[2];
                if (multiChannelAreaId === this._areaId && multiChannelStationId === params.get('station_id')) {
                    params.set('station_id', multiChannelSubStationId);
                    break;
                }
            }
        }

        const requestParams = this.buildPlaylistParam(params);
        for (let url of urls) {
            if (url.includes('?')) {
                requestUrls.push(`${url}&${requestParams}`);
            } else {
                requestUrls.push(`${url}?${requestParams}`);
            }
        }

        this.requestUrls = new URLIterator(requestUrls);
        const requestUrl = this.requestUrls.next().value;

        console.log(requestUrls);
        if(!requestUrls || requestUrls.length==0){
            if (this._callbacks && this._callbacks.onLoadStateError) {
                this._callbacks.onLoadStateError({});
            }
            throw 'Cannot find resource url';
        }

        if (this._hls) {
            return this.loadHlsjs(requestUrl);
        } else {
            return this.loadNative(requestUrl);
        }
    }

    private loadHlsjs(url: string) {
        console.log(`[loadHlsjs] ${url}`);

        this._hls.destroy();
        return new Promise((resolve, reject) => {
            this._hlsCallbacksBinded = false;
            this._isPlayStarted = false;
            this._hls = this.createHls();
            this.bind(this._callbacks);
            this._hls.attachMedia(this._audio);
            this._hls.on(Hls.Events.MEDIA_ATTACHED, () => {
                if (this._hls == null) {
                    return;
                }
                this._hls.loadSource(url);

                // このイベントの設定が生き残ってエラー時にすべらせた場合も再生ボタンの状態とかが切り替わる
                // ので、今のところはこれ消せない
                // 本当はonPlayStateChangeのコールバックでクライアント側で調整するのが本来あるべき姿だとは思う
                this._audio.addEventListener('canplay', () => {
                    this._isLive = this._hls.levels[0].details.live;
                    resolve();
                }, {
                    once: true
                });
            });
        });
    }

    private _mediaPlaylistUrl:any = null;
    private _programDateTime:any = null;

    private async loadNative(url: string) {
        console.log(`[loadNative] ${url}`);

        let headers = new Headers({
            "X-Radiko-AuthToken": this._authToken
        });

        while(url && url.length>0){
            // radiko外Web対応
            // iOSのNatvie Playerで再生させる際のリクエストパラメータ設定
            this._nativeState = new NativePlayerState();
            try {
                //const response = await fetch(`${url}&ext_device=1`, {mode: 'cors', headers: headers});
                const response = await _fetch(`${url}&ext_device=1`, {mode: 'cors', headers: headers}, PLAYLIST_HTTP_MAX_RETRY);
                const responseText = await response.text();
                const playlist = HlsParser.parse(responseText);
                if (playlist.isMasterPlaylist) {
                    // media playlist を取得
                    let variants:Array<any> = Object(playlist)["variants"];
                    if(variants.length){
                        let v:any = variants[0];
                        console.log(v.uri);
                        //const response = await fetch(`${v.uri}&mode=2`, {mode: 'cors'});
                        const response = await _fetch(`${v.uri}&mode=2`, {mode: 'cors'}, PLAYLIST_HTTP_MAX_RETRY);
                        const responseText = await response.text();
                        const mediaPlaylist = HlsParser.parse(responseText);
                        let segments:Array<any> = Object(mediaPlaylist)["segments"];
                        if(segments.length>0){
                            // 有効なセグメントが存在するならOK
                            this._nativeState.mediaPlaylistUrl = v.uri;
                            this._nativeState.programDatetime = segments[0].programDateTime;
                            this._nativeState.segment = segments[0];
                            this._nativeState.playlist = mediaPlaylist;
                            setTimeout(async ()=>{
                                if(this._callbacks && this._callbacks.onChunkListLoaded){
                                    this.chunkListLoadedHandler(this._callbacks.onChunkListLoaded)('segment loaded', null);
                                }
                            },1);
                            break; // 正常ケース
                        }
                    }
                }
            } catch (error) {
                console.log(error)
            }
            const requestUrl = this.requestUrls.next();
            if (requestUrl.done) break;
            url = requestUrl.value;
        }

        // m3u8のURLが取り出せなかったので読み込み不可にする
        if (!this._nativeState.isPlayable) {
            if (this._callbacks && this._callbacks.onLoadStateError) {
                this._callbacks.onLoadStateError({});
            }
            throw 'Cannot find resource url';
        }

        console.log(`mediaPlaylistUrl: ${this._nativeState.mediaPlaylistUrl}`);
        console.log(`programDateTime: ${this._nativeState.programDatetime}`);

        this.bind(this._callbacks);

        this.startPollingNative();

        return new Promise((resolve, reject) => {
            this._audio.addEventListener('loadstart', () => {
                this._isLive = Number.POSITIVE_INFINITY == this._audio.duration;
                resolve();
            });
            this._isPlayStarted = false;
            console.log(`_isPlayStarted=${this._isPlayStarted}`);
            this._is_idle = false;
            this._audio.setAttribute('src', `${this._nativeState.mediaPlaylistUrl}&mode=3`);
        });
    }

    private _timeoutPollingNative:any = null;
    private startPollingNative() {
        if(this._timeoutPollingNative) return;

        this._timeoutPollingNative = setTimeout(async () => {
            if(!this._timeoutPollingNative) return;
            this._timeoutPollingNative = null;

            if(!this._audio.paused && !this._audio.stopped){
                var isError = false;
                try {
                    const response = await _fetch(`${this._nativeState.mediaPlaylistUrl}&mode=2`, {mode: 'cors'}, PLAYLIST_HTTP_MAX_RETRY);
                    if(response.status>=400){
                        isError = true;
                    }
                    const responseText = await response.text();
                    const mediaPlaylist = HlsParser.parse(responseText);
                    let segments:Array<any> = Object(mediaPlaylist)["segments"];
                    if(segments.length>0){
                        if(!this._nativeState.segment || this._nativeState.segment.uri !== segments[0].uri){
                            // 新しいセグメントが追加された
                            this._nativeState.programDatetime = segments[0].programDateTime;
                            this._nativeState.segment = segments[0];
                            this._nativeState.playlist = mediaPlaylist;
                            console.log("programDateTime: " + this._nativeState.programDatetime);
    
                            setTimeout(async ()=>{
                                if(this._callbacks && this._callbacks.onFragmentPlaying){
                                    this.fragmentPlayingHandler(this._callbacks.onFragmentPlaying)('segment changed', null);
                                }
                            },1);
                            setTimeout(async ()=>{
                                if(this._callbacks && this._callbacks.onChunkListLoaded){
                                    this.chunkListLoadedHandler(this._callbacks.onChunkListLoaded)('segment loaded', null);
                                }
                            },1);
                        }
                    }
                } catch (error) {
                    isError = true;
                    console.log(error)
                }
                if(isError){
                    this.stop();
                    console.log("PollingNative: media playlist error!");
                    // 次のマスタープレイリスト
                    const requestUrl = this.requestUrls.next();
                    if (requestUrl.done === false) {
                        this.loadNative(requestUrl.value)
                            .then(() => {
                                this.play();
                            });
                        return;
                    }
                    if (this._callbacks && this._callbacks.onLoadStateError) {
                        this._callbacks.onLoadStateError({});
                    }
                    return;
                }
            }
    
            this.startPollingNative()
         },4000)

         this.startPollingNative();
    }
    private stopPollingNative() {
        if(this._timeoutPollingNative){
            clearTimeout(this._timeoutPollingNative);
            this._timeoutPollingNative = null;
        }
    }

    public set authToken(value: string) {
        this._authToken = value;
    }

    public set url(value: string) {
        this._url = value;
    }

    public get url(): string {
        return this._url;
    }

    public set areaId(value: string) {
        this._areaId = value;
    }

    public get areaId(): string {
        return this._areaId;
    }

    public get currentTime() {
        if (isNaN(this._audio.duration) && (<any>this).seektm) {
            return 0;
        }

        return this._audio.currentTime;
    }

    public get duration() {
        // ライブ再生では必要ないので0返す
        if (!(<any>this).totm) {
            return 0;
        }

        return (<any>this).totm.unix() - (<any>this).fttm.unix();
    }

    public set volume(value) {
        this._audio.volume = value;
    }

    public get volume() {
        return this._audio.volume;
    }

    public parse(url: string): Map<string, any> {
        let params: Map<string, any> = new Map<string, any>();

        const _queryString = url.match(/\?(.*)/);
        if (_queryString == null) {
            if (url.match(/^#(.+)/)) {
                params.set('station_id', RegExp.$1);
                params.set('l', this._length);
                return params;
            } else {
                throw 'Empty URL';
            }
        }

        Array.from(this.URLSearchParams(url)).forEach((pair) => {
            const [key, value] = pair;
            switch (key) {
                case 'ft':
                    const ft = moment(value, 'YYYYMMDDHHmmss');
                    params.set('start_at', ft.format('YYYYMMDDHHmmss'));
                    params.set('ft', ft.format('YYYYMMDDHHmmss'));
                    (<any>this).fttm = ft;
                    break;
                case 'to':
                    const to = moment(value, 'YYYYMMDDHHmmss');
                    params.set('end_at', to.format('YYYYMMDDHHmmss'));
                    params.set('to', to.format('YYYYMMDDHHmmss'));
                    (<any>this).totm = to;
                    break;
                case 'seek':
                    const seek = moment(value, 'YYYYMMDDHHmmss');
                    params.set('seek', seek.format('YYYYMMDDHHmmss'));
                    (<any>this).seektm = seek;
                    break;
                case 'l':
                    break;
                default:
                    params.set(key, value);
                    break;
            }
        });

        params.set('l', this._length);
        return params;
    }

    private URLSearchParams(url: string) {
        let queries = /\?(.*)/.exec(url);
        if (queries === null) {
            return new Map();
        }

        let searchParams = new Map();
        queries[1].split('&').forEach((param: any) => {
            let [key, value] = param.split('=');
            searchParams.set(key, value);
        });

        return searchParams;
    }

    public buildParams(params: Map<string, any>): string {
        return `${Array.from(params).map((value: any) => `${value[0]}=${value[1]}`).join('&')}`;
    }

    public buildPlaylistParam(params: Map<string, any>): string {
        this._connectionType = this._playlistManager.connectionType;
        params.set('type', this._connectionType);

        return this.buildParams(params);
    }

    public async getPlaylistUrl(stationId: string): Promise<string[]> {
        const response = await _fetch(
            `https://${this._host}/v3/station/stream/${this._appId}/${stationId}.xml`,
            {
                method: "get",
            }, PLAYLIST_HTTP_MAX_RETRY
        );

        const text = await response.text();
        const xml = new DOMParser().parseFromString(text, 'text/xml');

        return this._playlistManager.extractEndpoints(xml);
    }

    public play() {
        this._liveBaseSec = 0;
        this._audio.play();
        return new Promise((resolve, reject) => {
            // 再生開始を通知する
            this._audio.addEventListener('playing', () => {
                resolve();
            }, {
                once: true
            });
        });
    }

    public stop() {
        // 一時停止では無いので再生位置を初期化
        this.pause();
        this._audio.currentTime = 0;
    }

    public pause() {
        if (this._hls) {
            this._hls.stopLoad();
        }
        this._audio.pause();
        if(this._playStateChangeHandler){
            this._playStateChangeHandler({type:'pause'});
        }

        this._is_idle = true;
        this._audio.setAttribute('src', this._idleAudioSrc);
    }

    public seek(seconds: number) {
        this._audio.currentTime = seconds;
    }

    public isPlaying() {
        return !this._audio.paused;
    }

    public canSeek(): boolean {
        return Boolean(this._hls && this._isLive == false);
    }

    public seeking(): boolean {
        return this._audio.seekable.length != 0;
    }

    public paused(): boolean {
        return this._audio.paused;
    }

    public setMute(muted:boolean){
        this._isMuted = muted;
        if(this._audio){
            this._audio.muted = this._isMuted;
        }
    }

    public isMuted():boolean {
        return this._isMuted;
    }

    public destroy(): void {
        if (this._hls) {
            this._hls.destroy();
        } else {
            this._audio.preload = 'none';
            this._audio.src = null;
        }
    }

    public static isSupported() {
        return Hls.isSupported();
    }

    // 多チャンネル化対応
    // 再生しようとしている放送局が、ユーザーの都道府県のエリア内かどうかを判定する
    // 実際の処理は radiko-html/radiko.jp/apps/js/playerCommon.js の中の isStationInArea
    // 関数が渡されてnewされた時だけ関数を呼び出せるようにする
    private containStation(areaId: string, stationId: string): boolean {
        if (this._options && this._options.isStationInArea) {
            return this._options.isStationInArea(areaId, stationId);
        } else {
            return false;
        }
    }
}